import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { MetricCard, Loader, StatusBadge, Money, EmptyState } from "@/components/shared";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ShoppingBag, TrendingUp, IndianRupee, Percent, Wallet, Package, AlertTriangle, Users, Receipt, Truck } from "lucide-react";

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/manufacturer/dashboard").then((r) => setD(r.data)).catch(() => setD({})); }, []);
  if (!d) return <Loader />;

  const pending = d.verification_status !== "VERIFIED";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-semibold text-forest">Dashboard</h1>
        <StatusBadge status={d.verification_status} />
      </div>

      {pending && (
        <Alert className="border-amber-300 bg-amber-50" data-testid="verification-banner">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            Your account is <b>{d.verification_status?.replace(/_/g, " ")}</b>. You can list products only after VIPER OWL admin verifies your details.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard testid="m-today" label="Today's Orders" value={<span className="num">{d.todays_orders}</span>} icon={ShoppingBag} accent />
        <MetricCard testid="m-sales" label="Monthly Sales" value={<Money value={d.monthly_sales} />} icon={TrendingUp} accent />
        <MetricCard testid="m-gmv" label="GMV (Month)" value={<Money value={d.gmv} />} icon={IndianRupee} />
        <MetricCard testid="m-fees" label="Platform Fees" value={<Money value={d.script_fees} />} icon={Percent} />
        <MetricCard testid="m-settle" label="Pending Settlements" value={<Money value={d.pending_settlements} />} icon={Wallet} accent />
        <MetricCard testid="m-active" label="Active Products" value={<span className="num">{d.products_active}</span>} icon={Package} />
        <MetricCard testid="m-lowstock" label="Low Stock" value={<span className="num">{d.low_stock}</span>} icon={AlertTriangle} />
        <MetricCard testid="m-repeat" label="Repeat Retailers" value={<span className="num">{d.repeat_retailers}</span>} icon={Users} />
        <MetricCard testid="m-aov" label="Avg Order Value" value={<Money value={d.aov} />} icon={Receipt} />
        <MetricCard testid="m-dispatch" label="Awaiting Dispatch" value={<span className="num">{d.awaiting_dispatch}</span>} icon={Truck} accent />
      </div>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-heading text-lg font-medium text-forest">Recent transactions</h2>
          <Link to="/manufacturer/orders" className="text-sm text-olive hover:underline">All orders</Link>
        </div>
        {(!d.recent_orders || d.recent_orders.length === 0) ? (
          <EmptyState title="No orders yet." hint="Paid orders from retailers will appear here." />
        ) : (
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-psage/40 text-left"><tr className="overline text-forest">
                <th className="px-4 py-3">Order</th><th className="px-4 py-3">Retailer</th><th className="px-4 py-3">Total</th><th className="px-4 py-3">Status</th>
              </tr></thead>
              <tbody>
                {d.recent_orders.map((o) => (
                  <tr key={o.id} className="border-t border-border hover:bg-psage/20">
                    <td className="px-4 py-3"><Link to="/manufacturer/orders" className="num font-medium text-forest">{o.order_number}</Link></td>
                    <td className="px-4 py-3 text-ink/70">{o.retailer_name}</td>
                    <td className="px-4 py-3"><Money value={o.total} className="text-forest" /></td>
                    <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
